import { Information } from '@carbon/icons-react';
import { Button, Tooltip, useNotifications } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetEmailLastSent } from '../../../hooks/settings/admin/useGetEmailLastSent';
import { useGetFoodNotificationsSettings } from '../../../hooks/settings/admin/useGetFoodNotificationsSettings';
import { useResendEmailNotification } from '../../../hooks/settings/admin/useResendEmailNotification';
import { useUpdateFoodNotificationSettings } from '../../../hooks/settings/admin/useUpdateFoodNotificationSettings';
import type { FoodNotificationSettingsDto } from '../../../models/settings/admin/food-notification-settings.dto';
import { Input } from '../../../utils/components/hoc-components';
import { EmailNotificationResendType } from '../../../utils/enums/email-notification-resend-type.enum';
import {
  getCommonValidators,
  VALIDATION as V,
} from '../../../utils/inputValidators';
import { ResendButton } from '../../shared/buttons/ResendEmailNotificationButton';
import { FormSkeleton } from '../../shared/components/skeletons/FormSkeleton';

export function FoodPaymentNotificationSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notification = useNotifications();
  const updateSettings = useUpdateFoodNotificationSettings();
  const resendFoodNotification = useResendEmailNotification();
  const emailLastSentQueries = useGetEmailLastSent();
  const {
    data: settings,
    isPending: isSettingsPending,
    isError,
  } = useGetFoodNotificationsSettings();

  const methods = useForm<FoodNotificationSettingsDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      dayOfMonthForFirstEmail: 0,
      firstNotification: '',
      dayOfMonthForSecondEmail: 0,
      secondNotification: '',
    },
  });

  const { errors, isDirty } = methods.formState;

  const notificationMessageRules = useMemo(
    () => getCommonValidators([V.REQUIRED]),
    []
  );
  const daysRules = useMemo(
    () => getCommonValidators([V.REQUIRED, V.NUMBER_PATTERN, V.MIN_0]),
    []
  );

  const isSubmitting = updateSettings.isPending;
  const isResendBusy = resendFoodNotification.isPending;
  const hasErrors = Object.keys(errors).length > 0;
  const isEmpty = useCallback((value?: string) => !value || !value.trim(), []);

  const onSubmit = useCallback(
    (data: FoodNotificationSettingsDto) => {
      const payload: FoodNotificationSettingsDto = {
        ...data,
        dayOfMonthForFirstEmail: +data.dayOfMonthForFirstEmail,
        dayOfMonthForSecondEmail: +data.dayOfMonthForSecondEmail,
      };
      updateSettings
        .mutateAsync(payload)
        .then(() => {
          notification.success(
            t('SETTINGS.MESSAGES.NOTIFICATION_SETTINGS_UPDATED')
          );
          queryClient.invalidateQueries({
            queryKey: ['food-notification-settings'],
          });
          methods.reset(data);
        })
        .catch((error: any) => {
          notification.danger(t(`NETWORK_ERRORS.${error.errors?.[0]}`));
        });
    },
    [notification, t, queryClient, updateSettings, methods]
  );

  const onResend = useCallback(
    (type: EmailNotificationResendType) => {
      resendFoodNotification
        .mutateAsync(type)
        .then(() => {
          notification.success(
            t('SETTINGS.MESSAGES.NOTIFICATION_RESENT_SUCCESS')
          );
          queryClient.invalidateQueries({
            queryKey: ['email-last-sent', type],
          });
        })
        .catch((error: any) =>
          notification.danger(t(`NETWORK_ERRORS.${error.errors?.[0]}`))
        );
    },
    [notification, t, queryClient, resendFoodNotification]
  );

  useEffect(() => {
    if (settings) {
      methods.reset(settings);
    }
  }, [settings, methods]);

  if (isSettingsPending) {
    return <FormSkeleton />;
  }

  const lastSentFirst = !isError
    ? (emailLastSentQueries[3]?.data as string) ?? ''
    : '';

  const lastSentSecond = !isError
    ? (emailLastSentQueries[4]?.data as string) ?? ''
    : '';

  const notificationContent = methods.watch('firstNotification');

  return (
    <FormProvider {...methods}>
      <form className="settings pt-4" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="settings__section">
          <h2 className="settings__section__title">
            {t('SETTINGS.TITLES.FOOD_NOTIFICATION_FIRST')}
          </h2>

          <div className="notification-row">
            <label className="settings__section__item__label notification-row__label">
              {t('SETTINGS.LABELS.DAY_OF_MONTH')}
              <Tooltip
                position="right"
                message={t('SETTINGS.TOOLTIPS.MONTH_DAY_EMAIL_SENT')}
              >
                <Information size={16} className="settings-info-icon" />
              </Tooltip>
            </label>
            <Input
              name="dayOfMonthForFirstEmail"
              className="settings__section__item__input notification-row__input"
              rules={daysRules}
              placeholder={t('SETTINGS.LABELS.DAYS_COUNT') ?? ''}
            />
            <ResendButton
              isDirty={isDirty}
              isEmpty={isEmpty(notificationContent)}
              isSubmitting={isSubmitting}
              isResendLoading={
                isResendBusy &&
                resendFoodNotification.variables ===
                  EmailNotificationResendType.firstFoodPaymentReminder
              }
              isResendBusy={isResendBusy}
              onClick={() =>
                onResend(EmailNotificationResendType.firstFoodPaymentReminder)
              }
              tooltipMessage={t('SETTINGS.TOOLTIPS.SAVE_TO_ENABLE_RESEND')}
              buttonText={t('ACTIONS.RESEND')}
              lastSentDate={lastSentFirst}
            />
          </div>

          <div className="notification-row__textarea">
            <textarea
              className="settings__section__item__textarea"
              {...methods.register(
                'firstNotification',
                notificationMessageRules
              )}
              placeholder={t('SETTINGS.LABELS.EMAIL_CONTENT') ?? ''}
            />
          </div>
        </div>

        <div className="settings__section">
          <h2 className="settings__section__title">
            {t('SETTINGS.TITLES.FOOD_NOTIFICATION_SECOND')}
          </h2>

          <div className="notification-row">
            <label className="settings__section__item__label notification-row__label">
              {t('SETTINGS.LABELS.DAY_OF_MONTH')}
              <Tooltip
                position="right"
                message={t('SETTINGS.TOOLTIPS.MONTH_DAY_EMAIL_SENT')}
              >
                <Information size={16} className="settings-info-icon" />
              </Tooltip>
            </label>
            <Input
              name="dayOfMonthForSecondEmail"
              className="settings__section__item__input notification-row__input"
              rules={daysRules}
              placeholder={t('SETTINGS.LABELS.DAYS_COUNT') ?? ''}
            />
            <ResendButton
              isDirty={isDirty}
              isEmpty={isEmpty(notificationContent)}
              isSubmitting={isSubmitting}
              isResendLoading={
                isResendBusy &&
                resendFoodNotification.variables ===
                  EmailNotificationResendType.secondFoodPaymentReminder
              }
              isResendBusy={isResendBusy}
              onClick={() =>
                onResend(EmailNotificationResendType.secondFoodPaymentReminder)
              }
              tooltipMessage={t('SETTINGS.TOOLTIPS.SAVE_TO_ENABLE_RESEND')}
              buttonText={t('ACTIONS.RESEND')}
              lastSentDate={lastSentSecond}
            />
          </div>

          <div className="notification-row__textarea">
            <textarea
              className="settings__section__item__textarea"
              {...methods.register(
                'secondNotification',
                notificationMessageRules
              )}
              placeholder={t('SETTINGS.LABELS.EMAIL_CONTENT') ?? ''}
            />
          </div>
        </div>

        <div className="settings__actions">
          <Button
            className="settings__actions__button"
            type="submit"
            loading={isSubmitting}
            disabled={!isDirty || hasErrors || isSubmitting}
          >
            {t('ACTIONS.SAVE')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
